/**
 * Track markers (WORKBOARD I5, §12; rulings 25–26): how a team's knowledge of an enemy looks
 * at layers 0 / 1. Two kinds, by what the knowledge is:
 *   - `seen` — an identified unit (eyes, a drone): the team-coloured diamond the game already
 *     uses for spotted enemies.
 *   - `dot`  — a radar dot from a tower (ruling 26): a position with no identity, so no team
 *     colour — a neutral contact circle.
 * Both **fade with the track's confidence** (core/awareness.ts decays it; §12: "perishable
 * information is the awareness core's decay made visible", and spotted icons stop living
 * forever): fresh = solid; ageing = fainter, outlined; stale = a hollow dashed ring that has
 * grown a little, because an old position is a region, not a point.
 *
 * Pure style + a canvas drawer; the host caches textures per (kind, step, colour).
 */

export type TrackMarkerKind = 'seen' | 'dot' | 'mark';

export interface TrackMarkerStyle {
  opacity: number;   // 0..1 of the whole marker
  filled: boolean;   // solid body, or outline only
  dashed: boolean;   // the stale outline
  scale: number;     // ≥ 1: an older position is a wider guess
}

/** Confidence at or above this is fresh; below STALE it is a guess. */
export const MARKER_FRESH = 0.75;
export const MARKER_STALE = 0.4;
/** The dot's colour: no identity means no team colour. */
export const RADAR_DOT_COLOR = '#ffcf5a';

export function trackMarkerStyle(confidence: number): TrackMarkerStyle {
  const c = Math.max(0, Math.min(1, confidence));
  return {
    opacity: 0.3 + 0.7 * c,
    filled: c >= MARKER_STALE,
    dashed: c < MARKER_STALE,
    scale: 1 + 0.6 * (1 - c),
  };
}

/** Quantise confidence into a few steps so a host can cache one texture per step. */
export function markerStep(confidence: number, steps = 5): number {
  return Math.min(steps - 1, Math.floor(Math.max(0, Math.min(1, confidence)) * steps));   // 1.0 joins the top step
}

/**
 * Draw a marker centred in a square canvas context of `size` px. `color` is the team's unit
 * body colour for `seen`; ignored for `dot` (RADAR_DOT_COLOR).
 */
export function drawTrackMarker(g: CanvasRenderingContext2D, size: number, kind: TrackMarkerKind, confidence: number, color: string): void {
  const st = trackMarkerStyle(confidence);
  const fill = kind === 'dot' ? RADAR_DOT_COLOR : color;
  const r = (size * 0.3) * st.scale / 1.6;   // the largest (stalest) marker still fits the canvas
  g.clearRect(0, 0, size, size);
  g.save();
  g.translate(size / 2, size / 2);
  g.globalAlpha = st.opacity;
  g.lineWidth = Math.max(1.5, size * 0.06);
  if (st.dashed) g.setLineDash([size * 0.09, size * 0.07]);
  g.beginPath();
  if (kind === 'seen') { g.moveTo(0, -r); g.lineTo(r, 0); g.lineTo(0, r); g.lineTo(-r, 0); g.closePath(); }
  else if (kind === 'mark') { g.moveTo(-r, -r); g.lineTo(r, -r); g.lineTo(r, r); g.lineTo(-r, r); g.closePath(); g.moveTo(0, -r * 1.4); g.lineTo(0, -r); }   // X2: a square pin — a commander's fix
  else g.arc(0, 0, r * 0.85, 0, Math.PI * 2);
  if (st.filled) {
    g.fillStyle = '#0b0b18'; g.lineWidth += size * 0.06; g.strokeStyle = '#0b0b18'; g.stroke();   // dark backing, as the game's markers have
    g.fillStyle = fill; g.fill();
    if (confidence < MARKER_FRESH) { g.lineWidth = Math.max(1.5, size * 0.05); g.strokeStyle = '#e8eaf0'; g.stroke(); }
  } else {
    g.strokeStyle = '#0b0b18'; g.lineWidth += size * 0.05; g.stroke();
    g.strokeStyle = fill; g.lineWidth = Math.max(1.5, size * 0.06); g.stroke();
  }
  g.restore();
}
